<script lang="ts">
	import type { LocalBusiness } from "schema-dts";
	import emailIcon from "@core/assets/icons/email.svg?raw";
	import phoneIcon from "@core/assets/icons/phone.svg?raw";
	import Button from "../common/Button.svelte";
	import Icon from "../common/Icon.svelte";
	import Link from "../common/Link.svelte";
	import Modal from "../common/Modal.svelte";
	import StructuredData from "../common/StructuredData.svelte";
	import { ButtonVariant } from "../common/getButtonClass.js";

	const contactData = {
		title: "Head Spa Napohodu",
		address: "Hrnčířská 42/5, 733 01 Karviná 1",
		phone: "+420 727 955 703",
		email: "info@headspa-napohodu.cz",
	};

	const businessData: LocalBusiness = {
		"@type": "LocalBusiness",
		name: contactData.title,
		image: "https://headspa-napohodu.cz/favicon.ico",
		"@id": "https://headspa-napohodu.cz",
		url: "https://headspa-napohodu.cz",
		telephone: contactData.phone,
		email: contactData.email,
		address: {
			"@type": "PostalAddress",
			streetAddress: "Hrnčířská 42/5",
			addressLocality: "Karviná",
			postalCode: "733 01",
			addressCountry: "CZ",
		},
		geo: {
			"@type": "GeoCoordinates",
			latitude: 49.8543034,
			longitude: 18.5440014,
		},
		openingHoursSpecification: [
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: "Tuesday",
				opens: "10:00",
				closes: "15:00",
			},
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: "Wednesday",
				opens: "15:00",
				closes: "20:30",
			},
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: ["Thursday", "Friday"],
				opens: "14:00",
				closes: "18:30",
			},
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: "Saturday",
				opens: "09:00",
				closes: "14:00",
			},
		],
		priceRange: "1090-2490 CZK",
	};

	let showErrorModal = $state(false);
	let errorMessage = $state("");
	let showSuccessToast = $state(false);
	let successMessage = $state("");

	async function copyToClipboard(text: string, label: string): Promise<void> {
		try {
			await navigator.clipboard.writeText(text);
			successMessage = `${label} zkopírován do schránky`;
			showSuccessToast = true;
			setTimeout(() => {
				showSuccessToast = false;
			}, 3000);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error: unknown) {
			errorMessage = `Nepodařilo se zkopírovat ${label.toLowerCase()}. Zkuste to prosím znovu.`;
			showErrorModal = true;
		}
	}
</script>

<StructuredData data={businessData} />

<section class="flex flex-col justify-center items-center gap-8 sm:gap-12 md:gap-16 overflow-x-clip full-width">
	<div class="flex flex-col justify-center items-center gap-8 sm:gap-12 md:gap-16 w-full container-center px-4 xl:px-0">
		<h1 class="text-center w-full max-w-[57.5rem]">Kontakt</h1>

		<div class="relative w-full max-w-[50.0625rem]">
			<div
				class="w-full aspect-[801/524] rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[1.875rem] overflow-hidden shadow-[0px_48px_40px_-24px_rgba(0,0,0,0.15)]"
			>
				<iframe
					src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2572.369500452506!2d18.5440014!3d49.8543034!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4c591ee6d850363b%3A0xd947085e8dae27c2!2sHead%20Spa%20napohodu%20-%20Karvin%C3%A1!5e0!3m2!1scs!2scz!4v1767208096723!5m2!1scs!2scz"
					title="Mapa - Head Spa Napohodu"
					class="w-full h-full"
					style="border:0;"
					allowfullscreen
					loading="eager"
					referrerpolicy="no-referrer-when-downgrade"
				></iframe>
			</div>
		</div>

		<div class="flex flex-col items-center gap-3 sm:gap-4 md:gap-8 w-full max-w-[50.0625rem]">
			<h2
				class="font-heading font-bold text-lg sm:text-xl md:text-2xl lg:text-[2rem] leading-[110%] text-center text-hazelnut w-full"
			>
				{contactData.title}
			</h2>
			<p class="text-xs sm:text-sm md:text-base lg:text-p font-medium text-center text-midnight w-full">
				{contactData.address}
			</p>
			<Link
				target="_blank"
				href="https://www.google.com/maps?ll=49.854303,18.544001&z=16&t=m&hl=cs&gl=CZ&mapclient=embed&cid=15656491831773767618"
				variant={ButtonVariant.PrimaryMedium}>navigovat</Link
			>
		</div>

		<div class="flex flex-col gap-3 sm:gap-3 md:gap-4 w-full max-w-[25rem]">
			<div
				class="flex flex-col justify-center items-center p-4 sm:p-6 md:p-8 gap-3 sm:gap-4 md:gap-6 lg:gap-8 w-full bg-ivory-100 border border-ivory-300 rounded-[2rem] sm:rounded-[3rem] md:rounded-[4rem]"
			>
				<div class="flex flex-row items-center gap-2 sm:gap-3 md:gap-4">
					<Icon icon={phoneIcon} class="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 flex-shrink-0" />
					<span
						class="font-inter text-xs sm:text-sm md:text-base font-medium text-midnight leading-[140%] whitespace-nowrap"
					>
						{contactData.phone}
					</span>
				</div>
				<div class="flex flex-col sm:flex-row items-center gap-2 w-full">
					<Link
						href="tel:{contactData.phone}"
						class="inline-flex flex-row justify-center items-center uppercase px-3 sm:px-4 md:px-5 h-9 sm:h-10 md:h-12 gap-2 md:gap-4 duration-300 bg-copper-300 hover:bg-copper-200 shadow-[0px_6px_2px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0px_6px_8px_-4px_rgba(0,0,0,0.4)] rounded-2xl md:rounded-3xl text-xs sm:text-sm md:text-base text-ivory-100 transition-all w-full sm:flex-1"
						>zavolat</Link
					>
					<Button
						variant={ButtonVariant.SecondaryMedium}
						class="w-full sm:flex-1 h-9 sm:h-10 md:h-12 text-xs sm:text-sm md:text-base"
						onclick={(): void => {
							void copyToClipboard(contactData.phone, "Telefon");
						}}>kopírovat</Button
					>
				</div>
			</div>

			<div
				class="flex flex-col justify-center items-center p-4 sm:p-6 md:p-8 gap-3 sm:gap-4 md:gap-6 lg:gap-8 w-full bg-ivory-100 border border-ivory-300 rounded-[2rem] sm:rounded-[3rem] md:rounded-[4rem]"
			>
				<div class="flex flex-row items-center gap-2 sm:gap-3 md:gap-4">
					<Icon icon={emailIcon} class="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 flex-shrink-0" />
					<span
						class="font-inter text-xs sm:text-sm md:text-base font-medium text-midnight leading-[140%] whitespace-nowrap"
					>
						{contactData.email}
					</span>
				</div>
				<div class="flex flex-col sm:flex-row items-center gap-2 w-full">
					<Link
						href="mailto:{contactData.email}"
						class="inline-flex flex-row justify-center items-center uppercase px-3 sm:px-4 md:px-5 h-9 sm:h-10 md:h-12 gap-2 md:gap-4 duration-300 bg-copper-300 hover:bg-copper-200 shadow-[0px_6px_2px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0px_6px_8px_-4px_rgba(0,0,0,0.4)] rounded-2xl md:rounded-3xl text-xs sm:text-sm md:text-base text-ivory-100 transition-all w-full sm:flex-1"
						>napsat</Link
					>
					<Button
						variant={ButtonVariant.SecondaryMedium}
						class="w-full sm:flex-1 h-9 sm:h-10 md:h-12 text-xs sm:text-sm md:text-base"
						onclick={(): void => {
							void copyToClipboard(contactData.email, "Email");
						}}>kopírovat</Button
					>
				</div>
			</div>
		</div>
	</div>
</section>

{#if showSuccessToast}
	<div
		class="fixed bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-50 animate-slide-down px-4 w-full lg:w-fit max-w-md"
	>
		<div
			class="bg-copper-20-200 text-white px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl shadow-lg flex items-center gap-2 md:gap-3"
		>
			<svg class="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
			</svg>
			<span class="font-inter font-medium text-sm md:text-base">{successMessage}</span>
		</div>
	</div>
{/if}

<Modal bind:show={showErrorModal}>
	<div class="flex flex-col items-center gap-6 max-w-md">
		<div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
			<svg class="w-8 h-8 text-copper-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
			</svg>
		</div>
		<h3 class="font-heading font-bold text-2xl text-center text-hazelnut">Chyba</h3>
		<p class="font-inter text-base text-center text-midnight">{errorMessage}</p>
		<Button
			variant={ButtonVariant.PrimaryMedium}
			onclick={(): void => {
				showErrorModal = false;
			}}>Zavřít</Button
		>
	</div>
</Modal>

<style>
	@keyframes slide-down {
		from {
			opacity: 0;
			transform: translate(-50%, 100%);
		}
		to {
			opacity: 1;
			transform: translate(-50%, 0);
		}
	}

	.animate-slide-down {
		animation: slide-down 0.3s ease-out;
	}
</style>
